import React from 'react';
import { type LayerConfig } from '../../../types/metadata';
import { useMapStore } from '../store/useMapStore';
import { LayerSettingsModal } from './LayerSettingsModal';
import { DEFAULT_POINT_SETTINGS } from '../constants/pointSettings';
import { createSvgIcon } from '../utils/svgUtils';

interface PointLayerItemProps {
  layer: LayerConfig;
  settingsOpen?: boolean;
  onSettingsClosed?: () => void;
}

const EMPTY_FILTERS: any[] = [];

export const PointLayerItem: React.FC<PointLayerItemProps> = ({ layer, settingsOpen, onSettingsClosed }) => {
  const heatmapRange = useMapStore((state) => state.heatmapRanges[layer.id]);
  const activeFilters = useMapStore((state) => state.layerFilters[layer.id]) || EMPTY_FILTERS;
  const isOpen = !!settingsOpen;

  // Apply defaults when reading
  const pointSettings = { ...DEFAULT_POINT_SETTINGS, ...layer.pointSettings };

  // Create complete SVG icon
  const svgIcon = createSvgIcon({
    svg: pointSettings.svg,
    size: 14,
    color: pointSettings.color!,
    backgroundColor: pointSettings.borderColor ?? [255,255,255],
  });

  // Format large numbers with k suffix
  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      return `${Math.floor(num / 1000)}k`;
    }
    return num.toString();
  };

  // Create gradient CSS from heatmap colors (secondary to primary = low to high)
  const createGradient = () => {
    const primary = pointSettings.heatmapPrimaryColor;
    const secondary = pointSettings.heatmapSecondaryColor;
    return `linear-gradient(to right, rgb(${secondary.join(',')}), rgb(${primary.join(',')}))`;
  };

  return (
    <>
      <div className="detail-item">
        <div className="layer-icon-display">
          {(pointSettings.markerType === 'icon' || pointSettings.markerType === 'raw') && pointSettings.svg ? (
            <div dangerouslySetInnerHTML={{ __html: svgIcon }} />
          ) : (
            <svg 
              width="14" 
              height="14" 
              viewBox="0 0 24 24" 
              fill={`rgb(${pointSettings.color!.join(',')})`} 
              stroke={pointSettings.borderColor ? `rgb(${pointSettings.borderColor.join(',')})` : 'none'} 
              strokeWidth={pointSettings.borderColor ? pointSettings.borderSize : 0} 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
            </svg>
          )}
        </div>
        <span>Point Layer</span>
        {heatmapRange && heatmapRange.max > 0 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px', 
            marginLeft: '8px',
            fontSize: '11px',
            color: '#888'
          }}>
            <span style={{ fontWeight: 600 }}>{formatNumber(heatmapRange.min)}</span>
            <div style={{
              width: '40px',
              height: '12px',
              background: createGradient(),
              borderRadius: '2px',
              border: '1px solid #000'
            }} />
            <span style={{ fontWeight: 600 }}>{formatNumber(heatmapRange.max)}</span>
          </div>
        )}
      </div>
      <div className="detail-item">
        <span style={{ fontSize: '14px' }}>🔍</span>
        {activeFilters.length > 0 ? (
          <span style={{ color: '#1565c0' }}>
            {activeFilters.length} filter{activeFilters.length > 1 ? 's' : ''} applied
          </span>
        ) : (
          <span>No Filters - All items</span>
        )}
      </div>

      <LayerSettingsModal 
        isOpen={isOpen} 
        onClose={() => {
          onSettingsClosed?.();
        }} 
        layer={layer}
      />
    </>
  );
};
